import * as THREE from "three";
import { TERRAIN_MATERIALS as MAT } from "./terrain-materials";

type HeightFn = (x: number, z: number) => number;

function addMesh(group: THREE.Group, geometry: THREE.BufferGeometry, material: THREE.Material, x: number, y: number, z: number, rotationY = 0): THREE.Mesh {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.set(x, y, z);
  mesh.rotation.y = rotationY;
  group.add(mesh);
  return mesh;
}

export function addRoofUnits(
  group: THREE.Group,
  x: number,
  y: number,
  z: number,
  width: number,
  depth: number,
  rotation: number,
): void {
  const count = width > 16 ? 3 : width > 11 ? 2 : 1;
  const spacing = depth / (count + 1);
  for (let i = 0; i < count; i++) {
    const localZ = -depth * 0.25 + spacing * i;
    const rx = x + Math.cos(rotation) * (width * 0.12) - Math.sin(rotation) * localZ;
    const rz = z + Math.sin(rotation) * (width * 0.12) + Math.cos(rotation) * localZ;
    addMesh(group, new THREE.BoxGeometry(1.4, 0.8, 1.2), MAT.roofMetal, rx, y + 0.4, rz, rotation);
    addMesh(group, new THREE.CylinderGeometry(0.12, 0.12, 1.2, 6), MAT.roofMetal, rx + Math.sin(rotation) * 0.5, y + 1.1, rz - Math.cos(rotation) * 0.5);
  }
}

export function addFenceLoop(
  group: THREE.Group,
  x: number,
  z: number,
  width: number,
  depth: number,
  rotation: number,
  getHeight: HeightFn,
): void {
  const halfW = width * 0.5;
  const halfD = depth * 0.5;
  const fenceSegments: Array<[number, number, number, number]> = [
    [0, -halfD, width, 0.24],
    [0, halfD, width, 0.24],
    [-halfW, 0, 0.24, depth],
    [halfW, 0, 0.24, depth],
  ];

  for (const [lx, lz, w, d] of fenceSegments) {
    const wx = x + lx * Math.cos(rotation) - lz * Math.sin(rotation);
    const wz = z + lx * Math.sin(rotation) + lz * Math.cos(rotation);
    const wy = getHeight(wx, wz) + 0.65;
    addMesh(group, new THREE.BoxGeometry(w, 1.2, d), MAT.compound, wx, wy, wz, rotation);
  }
}

export function addUtilityPole(
  group: THREE.Group,
  x: number,
  z: number,
  rotation: number,
  getHeight: HeightFn,
): void {
  const y = getHeight(x, z);
  addMesh(group, new THREE.CylinderGeometry(0.14, 0.18, 8, 6), MAT.roofMetal, x, y + 4, z);
  addMesh(group, new THREE.BoxGeometry(2.2, 0.12, 0.18), MAT.roofMetal, x, y + 7.3, z, rotation);
}

export function addCrateCluster(group: THREE.Group, x: number, z: number, getHeight: HeightFn): void {
  const y = getHeight(x, z);
  const offsets = [[0, 0], [1.1, 0.4], [-0.8, 0.9]] as const;
  for (const [dx, dz] of offsets) {
    addMesh(group, new THREE.BoxGeometry(0.9, 0.8, 0.9), MAT.compound, x + dx, y + 0.4, z + dz);
  }
}

export function addSmallShed(
  group: THREE.Group,
  x: number,
  z: number,
  rotation: number,
  getHeight: HeightFn,
): void {
  const y = getHeight(x, z);
  addMesh(group, new THREE.BoxGeometry(3.4, 2.6, 2.8), MAT.rural, x, y + 1.3, z, rotation);
  addMesh(group, new THREE.BoxGeometry(3.9, 0.2, 3.2), MAT.roof, x, y + 2.7, z, rotation);
}
